import React from 'react';
import { useLocation } from 'react-router-dom';
import { useMenuContext } from '../providers/MenuProvider';
import { AccessDenied } from './ScopeRoute';

interface MenuGuardProps {
  children: React.ReactNode;
  /** Pages that have no menu of their own, mapped to the menu path(s) that open them (any one is enough) */
  aliases?: { match: RegExp; paths: string[] }[];
  /** Paths that skip the check (module landing, placeholders, pages without a menu): exact string or pattern */
  open?: (string | RegExp)[];
}

/**
 * Page-level menu check, by URL.
 * The module router only checks access to the module (e.g. /payroll), and hasAccess() lets every
 * sub-path of an accessible path through — the module path itself is in the list for anyone with
 * one menu of that module. So only page menus (/module/page...) count here.
 */
export function MenuGuard({ children, aliases = [], open = [] }: MenuGuardProps) {
  const { pathname } = useLocation();
  const { accessiblePaths, loading } = useMenuContext();
  const path = pathname.replace(/\/+$/, '');

  if (open.some((o) => (typeof o === 'string' ? o === path : o.test(path)))) return <>{children}</>;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const candidates = aliases.find((a) => a.match.test(path))?.paths ?? [path];
  const pageMenus = accessiblePaths.filter((a) => a.split('/').filter(Boolean).length >= 2);
  const allowed = candidates.some((c) => pageMenus.some((a) => c === a || c.startsWith(a + '/')));

  return allowed ? <>{children}</> : <AccessDenied />;
}
